import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Answer } from './api';
import { FeedbackCommentProps, FeedbackSectionId } from './utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulbExclamationOn } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { parseImpactDetailsAnswer } from './FeedbackImpactDetails';
import {
  INDICATOR_DETAILS_QUESTION_ID,
  INDICATOR_SUMMARY_QUESTION_ID,
} from 'npo/pages/NPOFeedback/utils';
import {
  IndicatorByService,
  IndicatorSectionKey,
  indicatorSectionsInOrder,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { RenderStageIcon } from './FeedbackImpactDetails/ImpactDetailStageIcons';
import { SectionIndicators } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import {
  GroupedComments,
  impactDetailsCommentsGroup,
} from 'npo/pages/BetaFeedback/Feedback/ImpactDetailComments';
import { ShowIndicatorFeedbackButton } from 'npo/pages/NPOFeedback/MakeEdits/components/ImpactTable/ShowIndicatorFeedback';
import FeedbackCommentsSection, {
  FeedbackCommentsSectionProps,
} from './FeedbackCommentsSection';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { ModelDetails } from './FeedbackModelButtons';
import RenderImpactIndicator from './RenderImpactIndicator';
import FeedbackModelComments from './FeedbackModelComments';
import { parseImpactSummaryAnswer } from 'npo/pages/NPOFeedback/FeedbackDetails/ImpactSummaryDetails';
import { IMPACT_SUMMARY_QUESTION_ID } from 'npo/pages/ReportBuilder/Components/SectionMultiRadio';
import { FeedbackApi } from '../api';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import ReportSectionNote from './FeedbackImpactReceipt/ReportSectionNote';
import { ReportAnswers } from './FeedbackReport';
import { NOTE_QUESTION_IDS } from 'npo/pages/ReportBuilder/constants';

const StyledStaticBanner = styled.div`
  display: flex;
  gap: ${SPACERS.small};
  padding: ${SPACERS.small};
  border-radius: ${SPACERS.tiny};
  margin-bottom: ${SPACERS.medium};
  border: 1px solid ${COLORS.highlightSubdued};
  background-color: ${COLORS.highlightBackground};
  svg {
    width: 24px;
    height: 24px;
    margin-top: ${SPACERS.extraTiny};
  }
  .heads-up-text {
    flex: 1;
  }
`;

const StaticBanner: React.FC = () => {
  return (
    <StyledStaticBanner>
      <FontAwesomeIcon
        color={COLORS.highlight}
        icon={faLightbulbExclamationOn}
      />
      <TrueImpactText className="heads-up-text">
        <strong>Heads up!</strong> For the Impact Model section, you can provide
        feedback on either the entire stage or on an individual indicator (or
        both). This feedback will display in the relevant context whenever the
        grantee is responding to feedback.
      </TrueImpactText>
    </StyledStaticBanner>
  );
};

const StyledRenderModel = styled.div`
  .section-comment-header {
    display: flex;
    align-items: center;
    gap: ${SPACERS.medium};
    justify-content: space-between;
  }
  .add-feedback {
    display: flex;
    cursor: pointer;
    align-items: center;
    gap: ${SPACERS.extraTiny};
  }
  .section-comments-textarea {
    margin-bottom: ${SPACERS.medium};
  }
`;

const RenderModel: React.FC<{
  answer: Answer;
  reportId: number;
  questionId: number;
  report: FeedbackApi;
  sectionId: FeedbackSectionId;
  sectionKey: IndicatorSectionKey;
  otherDetails: SectionIndicators;
  impactDetails: SectionIndicators;
  impactSummary: IndicatorByService;
  groupedComments: GroupedComments;
  onSuccessSave: FeedbackCommentsSectionProps['onSuccessSave'];
  onSuccessDelete: FeedbackCommentsSectionProps['onSuccessDelete'];
  addOrUpdateComment: FeedbackCommentsSectionProps['addOrUpdateComment'];
  deleteQuestionLevelComment: FeedbackCommentsSectionProps['deleteQuestionLevelComment'];
  updateQuestionLevelComment: FeedbackCommentsSectionProps['updateQuestionLevelComment'];
}> = ({
  answer,
  reportId,
  questionId,
  report,
  sectionId,
  sectionKey,
  otherDetails,
  impactDetails,
  impactSummary,
  groupedComments,
  onSuccessSave,
  onSuccessDelete,
  addOrUpdateComment,
  deleteQuestionLevelComment,
  updateQuestionLevelComment,
}) => {
  const { userType, email, name } = useUserActions();
  const [open, setOpen] = useState<boolean>(false);

  const handleToggle = useCallback(() => {
    setOpen(o => !o);
  }, [setOpen]);

  const comments = groupedComments.sections[sectionKey];
  const sectionDetails: ModelDetails = { sectionId: sectionKey };
  return (
    <StyledRenderModel>
      <div className="section-comment-header">
        <RenderStageIcon sectionKey={sectionKey} className="stage-name" />
        <ShowIndicatorFeedbackButton
          show={open}
          reverseIcon={true}
          comments={comments}
          nullFeedbackText="Add Stage Feedback"
          toggleShow={handleToggle}
        />
      </div>
      {open && (
        <FeedbackCommentsSection
          comments={comments}
          userType={userType}
          currentUserEmail={email}
          userName={name}
          reportId={reportId}
          questionId={questionId}
          details={JSON.stringify(sectionDetails)}
          feedbackSectionId={sectionId}
          answer={answer}
          hideModelButton={true}
          commentsWrapperClassName="section-comments"
          textareaWrapperClassName="section-comments-textarea"
          deleteQuestionLevelComment={deleteQuestionLevelComment}
          updateQuestionLevelComment={updateQuestionLevelComment}
          onSuccessDelete={onSuccessDelete}
          onSuccessSave={onSuccessSave}
          addOrUpdateComment={addOrUpdateComment}
        />
      )}
      {impactDetails[sectionKey].map(i => (
        <RenderImpactIndicator
          key={i.indicatorId}
          indicator={i}
          report={report}
          answer={answer}
          reportId={reportId}
          questionId={questionId}
          sectionId={sectionId}
          sectionKey={sectionKey}
          otherDetails={otherDetails}
          impactDetails={impactDetails}
          impactSummary={impactSummary}
          groupedComments={groupedComments}
          comments={groupedComments.indicators[sectionKey][i.indicatorId]}
          onSuccessSave={onSuccessSave}
          onSuccessDelete={onSuccessDelete}
          addOrUpdateComment={addOrUpdateComment}
          deleteQuestionLevelComment={deleteQuestionLevelComment}
          updateQuestionLevelComment={updateQuestionLevelComment}
        />
      ))}
    </StyledRenderModel>
  );
};

interface RenderImpactModelProps {
  answer: Answer;
  reportId: number;
  questionId: number;
  report: FeedbackApi;
  allAnswers: Answer[];
  sectionId: FeedbackSectionId;
  comments: FeedbackCommentProps[];
  onSuccessSave: FeedbackCommentsSectionProps['onSuccessSave'];
  onSuccessDelete: FeedbackCommentsSectionProps['onSuccessDelete'];
  addOrUpdateComment: FeedbackCommentsSectionProps['addOrUpdateComment'];
  deleteQuestionLevelComment: FeedbackCommentsSectionProps['deleteQuestionLevelComment'];
  updateQuestionLevelComment: FeedbackCommentsSectionProps['updateQuestionLevelComment'];
}

const StyledDiv = styled.div`
  padding: ${SPACERS.small} ${SPACERS.extraLarge} ${SPACERS.extraLarge}
    ${SPACERS.extraLarge};
  .show-model-comments {
    justify-content: center;
  }
  .model-comments {
    margin-top: ${SPACERS.tiny};
  }
`;

const RenderImpactModel: React.FC<RenderImpactModelProps> = ({
  answer,
  report,
  comments,
  reportId,
  sectionId,
  questionId,
  allAnswers,
  onSuccessSave,
  onSuccessDelete,
  addOrUpdateComment,
  deleteQuestionLevelComment,
  updateQuestionLevelComment,
}) => {
  const { impactSummary, impactDetails } = useIndicator();
  const fallbackImpactSummary = useMemo(() => {
    const a = allAnswers.find(
      x => x.question.dbId === IMPACT_SUMMARY_QUESTION_ID,
    );
    return parseImpactSummaryAnswer(a?.text);
  }, [allAnswers]);

  const otherDetails = useMemo(() => {
    const a = allAnswers.find(
      x => x.question.dbId === INDICATOR_DETAILS_QUESTION_ID,
    );
    return parseImpactDetailsAnswer(a?.text);
  }, [allAnswers]);

  if (questionId !== INDICATOR_SUMMARY_QUESTION_ID) {
    return null;
  }

  const fallbackImpactDetails = parseImpactDetailsAnswer(answer.text);

  if (!impactDetails || !impactSummary || !otherDetails) {
    return (
      <StyledDiv>
        <TrueImpactText>
          No questions were answered in this section or the model wasn&apos;t
          saved properly.
        </TrueImpactText>
      </StyledDiv>
    );
  }

  const groupedComments = impactDetailsCommentsGroup(impactDetails, comments);
  return (
    <StyledDiv>
      <StaticBanner />
      <FeedbackModelComments
        comments={groupedComments.overall}
        reportId={reportId}
        questionId={questionId}
        sectionId={sectionId}
        answer={answer}
        showButtonText="Overall Impact Model"
        nullFeedbackText="Add feedback to overall Impact Model"
        hideModelButton={true}
        showWrapperClassName="show-model-comments"
        commentsWrapperClassName="model-comments"
        textareaWrapperClassName="model-comments-textarea"
        deleteQuestionLevelComment={deleteQuestionLevelComment}
        updateQuestionLevelComment={updateQuestionLevelComment}
        onSuccessDelete={onSuccessDelete}
        onSuccessSave={onSuccessSave}
        addOrUpdateComment={addOrUpdateComment}
      />
      {indicatorSectionsInOrder.map(s => {
        if (impactDetails[s].length === 0) return null;
        return (
          <RenderModel
            key={s}
            sectionKey={s}
            otherDetails={otherDetails}
            impactSummary={impactSummary}
            impactDetails={impactDetails}
            groupedComments={groupedComments}
            answer={answer}
            reportId={reportId}
            questionId={questionId}
            report={report}
            sectionId={sectionId}
            onSuccessSave={onSuccessSave}
            onSuccessDelete={onSuccessDelete}
            addOrUpdateComment={addOrUpdateComment}
            deleteQuestionLevelComment={deleteQuestionLevelComment}
            updateQuestionLevelComment={updateQuestionLevelComment}
          />
        );
      })}
      <ReportSectionNote
        answers={{ indicator_details: allAnswers } as ReportAnswers}
        questionId={NOTE_QUESTION_IDS.impactDetails}
        label="Impact Model notes:"
      />
    </StyledDiv>
  );
};

export default RenderImpactModel;
